<template>
  <div>
    <b-row class="mb-3">
      <b-col cols="12" class="text-right">
        <b-button
          variant="info"
          size="sm"
          :disabled="reportData.length === 0"
          @click="excelExport"
        >
          <i class="fas fa-file-excel"></i>
        </b-button>
      </b-col>
    </b-row>

    <!-- Data Table -->
    <b-table
      id="PersonalDfTable"
      ref="PersonalDfTable"
      small
      sort-icon-left
      head-variant="light"
      :busy="tableBusy"
      :items="formattedRows"
      :fields="tableFields"
      :responsive="true"
      show-empty
    >
      <template #cell(date)="row">{{ row.value }}</template>
      <template #cell(df)="row">{{ formatNumber(row.value) }}</template>
      <template #cell(feePercent)="row">{{ row.value }}%</template>
      <template #cell(feeAmount)="row">{{ formatNumber(row.value) }}</template>
      <template #cell(netDf)="row">{{ formatNumber(row.value) }}</template>

      <template #custom-foot>
        <b-tr variant="secondary" class="text-bold text-right">
          <td class="text-left">รวม</td>
          <td></td>
          <td>{{ formatNumber(totalSummary.df) }}</td>
          <td></td>
          <td>{{ formatNumber(totalSummary.feeAmount) }}</td>
          <td>{{ formatNumber(totalSummary.netDf) }}</td>
        </b-tr>
      </template>
      <template #empty="">
        <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
      </template>
    </b-table>

    <!-- Summary by Payment Type -->
    <b-card v-if="summaryByPayment.length > 0" class="mt-4" no-body>
      <b-card-header>
        <strong>สรุปตามวิธีชำระ</strong>
      </b-card-header>
      <b-card-body>
        <b-table
          id="PersonalDfSummaryByPayment"
          :items="summaryByPayment"
          :fields="paymentSummaryFields"
          small
          head-variant="light"
          show-empty
        >
          <template #cell(df)="row">{{ formatNumber(row.value) }}</template>
          <template #cell(feePercent)="row">{{ row.value }}%</template>
          <template #cell(feeAmount)="row">{{ formatNumber(row.value) }}</template>
          <template #cell(netDf)="row">{{ formatNumber(row.value) }}</template>

          <template #custom-foot v-if="totalLabDf > 0">
            <b-tr class="text-right font-weight-bold">
              <td class="text-left">รวมค่าแลป</td>
              <td></td>
              <td></td>
              <td></td>
              <td>({{ formatNumber(totalLabDf) }})</td>
            </b-tr>
          </template>
        </b-table>

        <b-row class="mt-3">
          <b-col class="text-right">
            <p class="mb-1">รวม DF ทั้งหมด: <strong>{{ formatNumber(totalSummary.df) }}</strong></p>
            <p class="mb-1">รวมค่าธรรมเนียม: <strong>{{ formatNumber(totalSummary.feeAmount) }}</strong></p>
            <p v-if="totalLabDf > 0" class="mb-1">รวมค่าแลป: <strong>{{ formatNumber(totalLabDf) }}</strong></p>
            <p class="mb-0 font-weight-bold">รวม DF สุทธิ: <strong>{{ formatNumber(totalSummary.netDf) }}</strong></p>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Loading from "@/components/Loading";
import moment from "moment";
import XLSX from "xlsx";

export default {
  name: "PersonalDfByPaymentTypeReport",
  components: {
    Loading,
  },
  props: {
    doctorUid: {
      type: [String, Number],
      default: null,
    },
    dateRange: {
      type: Array,
      default: () => [],
    },
    labReport: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
      tableBusy: false,

      groupBy: "day", // default groupBy

      reportData: [],
      rawTableHeader: [],

      tableFields: [
        { key: "date", label: "วัน/เดือน/ปี" },
        { key: "paymentName", label: "วิธีชำระ" },
        { key: "df", label: "DF", class: "text-right" },
        { key: "feePercent", label: "ค่าธรรมเนียม %", class: "text-right" },
        { key: "feeAmount", label: "ค่าธรรมเนียม", class: "text-right" },
        { key: "netDf", label: "DF สุทธิ", class: "text-right font-weight-bold" },
      ],

      paymentSummaryFields: [
        { key: "name", label: "วิธีชำระ" },
        { key: "df", label: "DF", class: "text-right" },
        { key: "feePercent", label: "ค่าธรรมเนียม %", class: "text-right" },
        { key: "feeAmount", label: "ค่าธรรมเนียม", class: "text-right" },
        { key: "netDf", label: "DF สุทธิ", class: "text-right font-weight-bold" },
      ],
    };
  },
  created() {
    this.init();
  },
  computed: {
    ...mapGetters({
      paymentMethodList: "moduleUser/getPaymentMethodList",
    }),

    dateFrom() {
      return this.dateRange[0]
        ? moment(this.dateRange[0]).startOf("day").format()
        : "";
    },
    dateTo() {
      return this.dateRange[1]
        ? moment(this.dateRange[1]).endOf("day").format()
        : "";
    },

    canFetch() {
      return this.doctorUid && this.dateRange.length === 2;
    },

    // Format data into rows
    formattedRows() {
      const rows = [];

      this.reportData.forEach(doctor => {
        doctor.dates.forEach(d => {
          d.dfByPaymentType.forEach(pm => {
            if (pm.value > 0) {
              const feePercent = this.getProcessingFee(pm.keyName);
              const feeAmount = (pm.value * feePercent) / 100;

              rows.push({
                date: this.displayDate(d.creationDt),
                paymentName: pm.name,
                keyName: pm.keyName,
                df: pm.value,
                feePercent: feePercent,
                feeAmount: feeAmount,
                netDf: pm.value - feeAmount,
              });
            }
          });
        });
      });

      return rows;
    },

    // Summary by payment type
    summaryByPayment() {
      const paymentTotals = {};

      this.formattedRows.forEach(row => {
        if (!paymentTotals[row.keyName]) {
          paymentTotals[row.keyName] = {
            name: row.paymentName,
            keyName: row.keyName,
            df: 0,
            feePercent: row.feePercent,
            feeAmount: 0,
            netDf: 0,
          };
        }
        paymentTotals[row.keyName].df += row.df;
        paymentTotals[row.keyName].feeAmount += row.feeAmount;
        paymentTotals[row.keyName].netDf += row.netDf;
      });

      return Object.values(paymentTotals);
    },

    totalLabDf() {
      return this.labReport.reduce((sum, item) => sum + (item.dfAmount || 0), 0);
    },

    // Total summary
    totalSummary() {
      let totalDf = 0;
      let totalFee = 0;

      this.formattedRows.forEach(row => {
        totalDf += row.df;
        totalFee += row.feeAmount;
      });

      return {
        df: totalDf,
        feeAmount: totalFee,
        labDf: this.totalLabDf,
        netDf: totalDf - totalFee - this.totalLabDf,
      };
    },
  },
  watch: {
    doctorUid() {
      this.reportData = [];
    },
    dateRange() {
      this.reportData = [];
    },
  },
  methods: {
    ...mapActions({
      fetchDfByPaymentType: "moduleReport/fetchDfByPaymentType",
      fetchPaymentMethodList: "moduleUser/fetchPaymentMethodList",
    }),

    init() {
      this.fetchPaymentMethodList({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      });
    },

    formatNumber(num) {
      if (num === null || num === undefined) return "-";
      return num.toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    displayDate(date) {
      if (this.groupBy === "year")
        return "ปี " + moment(date, "YYYY").locale("th").format("YYYY");
      if (this.groupBy === "month")
        return moment(date, "YYYY-MM").locale("th").format("MMM YY");
      return moment(date, "YYYY-MM-DD").locale("th").format("D MMM YY");
    },

    getProcessingFee(keyName) {
      const method = this.paymentMethodList.find(m => m.id === keyName);
      return method?.processingFee || 0;
    },

    async fetchReport() {
      if (!this.canFetch) return;

      this.isLoading = true;
      this.tableBusy = true;

      try {
        const res = await this.fetchDfByPaymentType({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
          groupBy: this.groupBy,
          actionUid: this.doctorUid,
        });

        this.reportData = res.data.item || [];
        this.rawTableHeader = res.data.header?.receiptPayment || [];
      } catch (err) {
        console.error(err);
      } finally {
        this.isLoading = false;
        this.tableBusy = false;
      }
    },

    excelExport() {
      document.querySelectorAll(".sr-only").forEach(e => e.remove());

      let wb = XLSX.utils.book_new();
      let filename = "personal-df-by-payment-type-report.xlsx";

      const tbl = document.getElementById("PersonalDfTable");
      if (tbl) {
        const ws = XLSX.utils.table_to_sheet(tbl);
        XLSX.utils.book_append_sheet(wb, ws, "DF ตามวิธีชำระ");
      }

      const summaryTbl = document.getElementById("PersonalDfSummaryByPayment");
      if (summaryTbl) {
        const ws = XLSX.utils.table_to_sheet(summaryTbl);
        XLSX.utils.book_append_sheet(wb, ws, "สรุปตามวิธีชำระ");
      }

      XLSX.writeFile(wb, filename);
    },

    // Methods exposed for parent component
    getSummary() {
      return this.totalSummary;
    },
    getSummaryByPayment() {
      return this.summaryByPayment;
    },
  },
};
</script>

<style>
</style>
